import type { ConfigProps, DefaultContributorProps } from '../../configTypes';
import executeGraphQlRequest from './executeGraphQlRequest';

export const pageQuery = (): string => `
query DefaultContributorQuery {
  contributorSettings {
    defaultContributor {
      content
      featuredImage
      id
      slug
      title
      career
    }
  }
}
`;

/**
 * In-flight promises keyed by cmsUrl so concurrent callers (e.g. multiple
 * shortcodes enriching in Promise.all) share one GraphQL request. Entries are
 * removed when the request settles so later renders fetch fresh CMS data.
 */
const inflightByCmsUrl = new Map<
  string,
  Promise<DefaultContributorProps | null>
>();

/** Clears in-flight dedupe state between tests. */
export function clearDefaultContributorInflightForTests(): void {
  inflightByCmsUrl.clear();
}

export default async function fetchDefaultContributor(
  config: ConfigProps,
): Promise<DefaultContributorProps | null> {
  const key = config.cmsUrl;
  const existing = inflightByCmsUrl.get(key);
  if (existing) {
    return existing;
  }

  const request = (async (): Promise<DefaultContributorProps | null> => {
    const data = await executeGraphQlRequest<{
      contributorSettings: {
        defaultContributor: DefaultContributorProps | null;
      };
    }>({
      cmsUrl: config.cmsUrl,
      query: pageQuery(),
    });
    return data.contributorSettings.defaultContributor ?? null;
  })().finally(() => {
    inflightByCmsUrl.delete(key);
  });

  inflightByCmsUrl.set(key, request);
  return request;
}
